/**
 * How this user hears about a new release: 'standard' (the app switches over
 * quietly between pages, and a card appears only after a long stay on one
 * page) or 'quiet' (no card, just an "Update ready" pill on the dashboard).
 * The rules live in lib/appUpdates.ts.
 *
 * Reads `profiles.update_notices` and writes it through the self-only RPC
 * `set_update_notices`, the same shape as the toast position preference.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { DEFAULT_UPDATE_NOTICE_MODE, isUpdateNoticeMode, type UpdateNoticeMode } from '@/lib/appUpdates';

export const UPDATE_NOTICES_QUERY_KEY = ['profile', 'update-notices'] as const;

type RpcClient = {
  rpc: (fn: string, args: Record<string, unknown>) => PromiseLike<{ error: { message: string } | null }>;
};

export function useUpdateNotices() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const queryKey = [...UPDATE_NOTICES_QUERY_KEY, user?.id];

  const query = useQuery({
    queryKey,
    enabled: !!user?.id,
    queryFn: async (): Promise<UpdateNoticeMode> => {
      const { data, error } = await supabase
        .from('profiles')
        .select('update_notices' as never)
        .eq('id', user!.id)
        .maybeSingle();
      if (error) throw error;
      const row = data as { update_notices?: unknown } | null;
      // A row without the column yet (pre-migration) reads as the default.
      return isUpdateNoticeMode(row?.update_notices) ? row.update_notices : DEFAULT_UPDATE_NOTICE_MODE;
    },
  });

  const mutation = useMutation({
    mutationFn: async (mode: UpdateNoticeMode) => {
      const { error } = await (supabase as unknown as RpcClient).rpc('set_update_notices', { p_mode: mode });
      if (error) throw new Error(error.message);
      return mode;
    },
    onMutate: async (mode) => {
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<UpdateNoticeMode>(queryKey);
      qc.setQueryData(queryKey, mode);
      return { previous };
    },
    onError: (err, _mode, ctx) => {
      // No snapshot (the initial read had failed) means there is nothing to
      // restore; refetch so a rejected choice never stays applied.
      if (ctx?.previous === undefined) void qc.invalidateQueries({ queryKey });
      else qc.setQueryData(queryKey, ctx.previous);
      toast.error(err instanceof Error ? err.message : 'Could not save that preference');
    },
    onSuccess: (mode) => {
      toast.success(mode === 'quiet' ? 'No update pop-ups: look for Update ready on your dashboard' : 'Update pop-ups are back on');
      qc.invalidateQueries({ queryKey: ['profile'] });
    },
  });

  return {
    mode: query.data ?? DEFAULT_UPDATE_NOTICE_MODE,
    isLoading: query.isLoading,
    isSaving: mutation.isPending,
    setMode: (mode: UpdateNoticeMode) => mutation.mutate(mode),
  };
}
